import React, { useState, useCallback, useEffect } from 'react';
import { Panel } from '../panels/Panel';
import { HierarchyToolbar } from './HierarchyToolbar';
import { HierarchyTree } from './HierarchyTree';
import { usePanelStore } from '../../store/panelStore';
import { useSceneStore } from '../../store/sceneStore';

export const HierarchyPanel: React.FC = () => {
  const panel = usePanelStore((state) => state.panels['hierarchy']);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle global keyboard shortcuts for the panel
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only handle if hierarchy panel is focused or no other panel is actively receiving input
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement ||
        activeElement?.getAttribute('contenteditable') === 'true' ||
        activeElement?.closest('.monaco-editor') ||
        activeElement?.closest('[data-script-editor]');

      if (isInputFocused) return;

      // Undo: Ctrl+Z
      if (e.key === 'z' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
        e.preventDefault();
        useSceneStore.temporal.getState().undo();
        return;
      }
      // Redo: Ctrl+Y or Ctrl+Shift+Z
      if (
        (e.key === 'y' && (e.ctrlKey || e.metaKey)) ||
        (e.key === 'z' && (e.ctrlKey || e.metaKey) && e.shiftKey)
      ) {
        e.preventDefault();
        useSceneStore.temporal.getState().redo();
        return;
      }

      const {
        selectedIds,
        deleteSelected,
        copySelected,
        cutSelected,
        paste,
        duplicateGameObject,
        selectAll,
        clearSelection,
      } = useSceneStore.getState();

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          if (selectedIds.length > 0) {
            e.preventDefault();
            deleteSelected();
          }
          break;
        case 'c':
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.preventDefault();
            copySelected();
          }
          break;
        case 'x':
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.preventDefault();
            cutSelected();
          }
          break;
        case 'v':
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.preventDefault();
            paste();
          }
          break;
        case 'd':
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            if (selectedIds.length === 1) {
              e.preventDefault();
              duplicateGameObject(selectedIds[0]);
            }
          }
          break;
        case 'a':
          if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.preventDefault();
            selectAll();
          }
          break;
        case 'Escape': {
          const { focusedContainerId } = useSceneStore.getState();
          if (focusedContainerId) {
            useSceneStore.getState().exitContainer();
          } else {
            clearSelection();
          }
          break;
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  if (!panel.visible) {
    return null;
  }

  return (
    <Panel panel={panel} className="flex flex-col h-full">
      <HierarchyToolbar searchQuery={searchQuery} onSearchChange={handleSearchChange} />
      <HierarchyTree searchQuery={searchQuery} />
    </Panel>
  );
};
